import { useState, useEffect } from "react";
import styled from "styled-components";
import { fmtPrice, fmtCompactUsd, fmtChange } from "./utils";
import { getWatchlistApi } from "./getWatchlistApi";
import { TELEGRAM_URL } from "./telegram";

// Matched to the cache window in netlify/functions/watchlist.mjs, and to the
// Watchlist page's own interval. Polling faster asks the CDN the same question
// repeatedly for one new answer.
const REFRESH_MS = 5 * 60 * 1000;

// Below this the volume and market cap are dropped and the bar carries the
// price alone. Three figures need about 430px before they start colliding with
// each other, and a phone has better things to do with that width.
const NARROW = "560px";

// Bitcoin orange at 12% over the page background, which lands on #3c3024.
//
// Checked rather than eyeballed, because this sits on every page: against that
// bar the value text runs 10.6:1, the labels 5.5:1 and the orange itself
// 5.6:1, so all three clear WCAG AA. A heavier tint still passes but stops
// reading as a stripe and starts reading as a banner.
const BITCOIN_ORANGE = "#f7931a";

const Bar = styled.div`
  background: rgba(247, 147, 26, 0.12);
  border-bottom: 1px solid rgba(247, 147, 26, 0.3);
  padding: 0.4rem 20px;
  display: flex;
  align-items: baseline;
  gap: 1.5rem;
  font-size: 0.85rem;
  line-height: 1.4;
  /* the header above is sticky and sets z-index 100; the bar scrolls away with
     the page rather than stacking under it */

  @media (max-width: 650px) {
    padding-left: 12px;
    padding-right: 12px;
  }

  @media (max-width: ${NARROW}) {
    gap: 0.75rem;

    .wide {
      display: none;
    }
  }
`;

const Item = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  white-space: nowrap;
`;

const Label = styled.span`
  color: var(--secondary-color);
  font-size: 0.78rem;
`;

const Ticker = styled.span`
  color: ${BITCOIN_ORANGE};
  font-weight: 700;
`;

const Value = styled.span`
  color: var(--font-color);
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

// A quieter weight than the price: these are context, and giving all three
// figures the same emphasis makes the bar read as a row of equals rather than
// a price with two footnotes.
const SubValue = styled.span`
  color: var(--font-color);
  font-variant-numeric: tabular-nums;
`;

// Below this the link says "Telegram" rather than "Whale alerts on Telegram".
// The full label measures 196px and the tightest width is not the narrowest
// one: at 561px the three figures are all still on the bar and leave 86px, so
// it overflowed the page there before this. The short label is 76px and fits
// everywhere down to 320px.
const SHORT_LABEL = "700px";

// The site-wide way in to the Telegram channel - the whale panel on Bitcoin
// Live carries the other one, on the page whose content the channel posts.
//
// It is here rather than in the header nav because the nav is full. Measured at
// 992px - the narrowest width that still shows it - the six links need 665px,
// have 544px and already wrap onto two lines; a seventh makes that worse. This
// bar runs the full width of every page and carries nothing on its right-hand
// half at any size.
//
// Bordered rather than plain text because links on this site inherit the colour
// of the text around them and carry no underline, so a bare one here would read
// as a label. The border is the one the whale panel's threshold buttons use.
//
// #62c4ff is the page's own link colour and measures 6.6:1 against this bar's
// background, clearing WCAG AA the way the figures beside it do.
const TelegramLink = styled.a`
  margin-left: auto;
  padding: 0 0.45rem;
  border: 1px solid rgba(98, 196, 255, 0.4);
  border-radius: 3px;
  font-size: 0.78rem;
  white-space: nowrap;
  color: var(--primary-color);
  text-decoration: none;

  &:hover {
    color: var(--primary-color);
    border-color: var(--primary-color);
  }

  .long {
    @media (max-width: ${SHORT_LABEL}) {
      display: none;
    }
  }
`;

// The 24 hour move, in the same up and down colours the watchlist and the whale
// panel use - but lightened, and that is not a style choice.
//
// The watchlist picked #26a65b and #ff3c74 against the page background. This bar
// is 12% Bitcoin orange over that background, which is lighter, and on it those
// two measure 4.08:1 and 3.75:1 - both under the 4.5:1 that WCAG AA asks for
// normal text. Lifting each by the smallest step that clears it gives 4.91:1
// and 4.75:1, with the hues unchanged, so the bar reads as the same language as
// the table without failing on contrast.
//
// Coloured by the rounded figure rather than the raw one, exactly as the
// watchlist does it: -0.004% renders as "0.00%" and must not be painted red.
const Change = styled.span`
  font-variant-numeric: tabular-nums;
  color: ${({ $value }) => {
    const hundredths = Math.round(($value ?? 0) * 100);
    if (hundredths > 0) return "#2ab764";
    if (hundredths < 0) return "#ff6b96";
    return "var(--secondary-color)";
  }};
`;

// The bar holds its own height whatever it is saying, so the page below does
// not jump when the first figures land. An em-dash placeholder does that and
// also says plainly that a number is missing rather than zero.
const PLACEHOLDER = "—";

const BtcTicker = () => {
  // null until the first answer, so a failed first load and a slow one can be
  // told apart - and neither renders a misleading $0.00.
  const [btc, setBtc] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const doFetch = async () => {
      const coins = await getWatchlistApi();
      if (cancelled || coins === null) return;
      const found = coins.find((coin) => coin.symbol === "BTC");
      // A refresh that comes back without Bitcoin in it leaves the last good
      // figures alone rather than blanking the bar on every page at once.
      if (found) setBtc(found);
    };

    doFetch();
    const intervalId = setInterval(doFetch, REFRESH_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  return (
    <Bar aria-label="Bitcoin price, 24 hour volume and market cap, and the whale alert channel">
      <Item>
        <Ticker>BTC</Ticker>
        <Value>{btc ? fmtPrice(btc.price) : PLACEHOLDER}</Value>
        {/* Stays on a phone while the volume and market cap drop. A price with
            no direction is half a reading, and "+5.79%" costs six characters
            where those two cost thirty. */}
        <Change $value={btc?.change24h}>
          {btc ? fmtChange(btc.change24h) : PLACEHOLDER}
        </Change>
      </Item>
      <Item className="wide">
        <Label>24h Vol</Label>
        <SubValue>{btc ? fmtCompactUsd(btc.volume24h) : PLACEHOLDER}</SubValue>
      </Item>
      <Item className="wide">
        <Label>Mkt Cap</Label>
        <SubValue>{btc ? fmtCompactUsd(btc.marketCap) : PLACEHOLDER}</SubValue>
      </Item>
      {/* The accessible name stays the full sentence at every width, so the
          short label is a visual shortening rather than a different link. */}
      <TelegramLink
        href={TELEGRAM_URL}
        target="_blank"
        rel="noreferrer noopener"
        aria-label="Whale alerts on Telegram"
      >
        <span className="long">Whale alerts on </span>Telegram
      </TelegramLink>
    </Bar>
  );
};

export default BtcTicker;
